import { useEffect, useRef, useState } from 'react';
import { EMAIL_TEMPLATE_TAG_GROUPS, appendCapiumTag } from '@/core/models/capium-tags';
import type { EmailTemplateRow } from '@/core/models/firm-settings.models';
import type { RichTextConfig } from '@/core/models/richText';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { firmSettingsService } from '@/core/services/firmSettings';
import { NoteTagsTab } from '@/features/settings-b/NoteTagsTab';
import { Button } from '@/kit/Button';
import { ConfirmPopup, type ConfirmPopupData } from '@/kit/ConfirmPopup';
import { openDialog } from '@/kit/dialog';
import { InputField } from '@/kit/FormField';
import { useDirtyForm } from '@/kit/hooks/useDirtyForm';
import { Icon } from '@/kit/Icon';
import { InfoHint } from '@/kit/InfoHint';
import { LeaveGuard } from '@/kit/LeaveGuard';
import { PageHeader } from '@/kit/PageHeader';
import { RichTextEditor } from '@/kit/RichTextEditor';
import { TabGroup } from '@/kit/Tabs';
import { Tooltip } from '@/kit/Tooltip';

const editorConfig: RichTextConfig = {
  editable: true,
  spellcheck: true,
  height: '250',
  minHeight: '0',
  maxHeight: 'auto',
  width: 'auto',
  minWidth: '0',
  translate: 'yes',
  enableToolbar: true,
  showToolbar: true,
  placeholder: 'Enter the email body...',
  defaultParagraphSeparator: '',
  sanitize: true,
  toolbarPosition: 'bottom',
  toolbarHiddenButtons: [
    [
      'fontName',
      'fontSize',
      'backgroundColor',
      'strikeThrough',
      'customClasses',
      'insertHorizontalRule',
      'removeFormat',
      'heading',
      'insertImage',
      'insertVideo',
      'fonts',
    ],
  ],
};

/** 3.0's email template subject limit; the API enforces the same (FirmSettingsRules.MaxEmailSubjectLength). */
const EMAIL_SUBJECT_MAX_LENGTH = 80;

const TOOLTIP =
  'Accountants can change the subject and body used when the app emails clients.\n' +
  '                Reset puts a template back to the Capium default.';

const DEFAULT_CIC_REPORT_TEMPLATE: EmailTemplateRow = {
  key: 'cic-report',
  name: 'CIC Report',
  subject: 'CIC Report - [company_name]',
  body: '<p>Dear [contact_name],</p><p>Please find attached the CIC Report for [company_name] for the period ended [date_period_end].</p>',
  isCustomised: false,
  updatedAtUtc: null,
};

function ensureTemplates(list: EmailTemplateRow[]): EmailTemplateRow[] {
  if (list.length === 0) return list;
  const hasCic = list.some((t) => t.key.toLowerCase().includes('cic') || t.name.toLowerCase().includes('cic'));
  if (hasCic) return list;

  // Insert CIC Report after Accounts Report if present, else append
  const accountsIdx = list.findIndex(
    (t) => t.name.toLowerCase().includes('accounts report') || t.key.toLowerCase().includes('accounts-report'),
  );
  const copy = [...list];
  if (accountsIdx >= 0) {
    copy.splice(accountsIdx + 1, 0, DEFAULT_CIC_REPORT_TEMPLATE);
  } else {
    copy.push(DEFAULT_CIC_REPORT_TEMPLATE);
  }
  return copy;
}

interface TemplateText {
  subject: string;
  body: string;
}

/** The editor may hand back the same HTML with different surrounding whitespace; that is not a change. */
const sameTemplateText = (a: TemplateText, b: TemplateText) =>
  a.subject === b.subject && a.body.trim() === b.body.trim();

/**
 * The firm's email templates (seeded defaults, overridable per firm). Design review 2 Nov 2026: always editable - once the
 * subject or body of the selected template differs from what was saved, the header offers Save and Discard.
 */
export function Emialtemplates() {
  const [templates, setTemplates] = useState<EmailTemplateRow[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { values, setValues, dirty, discard, markSaved } = useDirtyForm<TemplateText>(
    { subject: '', body: '' },
    sameTemplateText,
  );
  const { subject, body } = values;
  const setSubject = (next: string) => setValues((current) => ({ ...current, subject: next }));
  const setBody = (next: string) => setValues((current) => ({ ...current, body: next }));
  const [isSaving, setIsSaving] = useState(false);
  const selectedIndexRef = useRef(0);
  const templatesRef = useRef<EmailTemplateRow[]>([]);
  const mounted = useRef(true);

  const selected: EmailTemplateRow | null = templates.length > 0 ? (templates[selectedIndex] ?? null) : null;

  /** Copies the selected template's subject/body into the editable fields. */
  const bind = (list: EmailTemplateRow[], index: number) => {
    const template = list.length > 0 ? (list[index] ?? null) : null;
    markSaved({ subject: template?.subject ?? '', body: template?.body ?? '' });
  };

  const replaceTemplates = (list: EmailTemplateRow[]) => {
    const ensured = ensureTemplates(list);
    templatesRef.current = ensured;
    setTemplates(ensured);
  };

  useEffect(() => {
    mounted.current = true;
    firmSettingsService.getEmailTemplates().then(
      (list) => {
        if (!mounted.current) return;
        const ensured = ensureTemplates(list);
        replaceTemplates(ensured);
        selectedIndexRef.current = Math.min(selectedIndexRef.current, Math.max(0, ensured.length - 1));
        setSelectedIndex(selectedIndexRef.current);
        bind(ensured, selectedIndexRef.current);
      },
      (error) => notifyError(error),
    );
    return () => {
      mounted.current = false;
    };
  }, []);

  const switchTo = (index: number) => {
    selectedIndexRef.current = index;
    setSelectedIndex(index);
    bind(templatesRef.current, index);
  };

  /** Another template's tab would drop the unsaved text, so it asks first. */
  const onTabChange = (index: number) => {
    if (index === selectedIndexRef.current) return;
    if (!dirty) {
      switchTo(index);
      return;
    }
    void openDialog<ConfirmPopupData, boolean>(ConfirmPopup, {
      data: {
        title: 'Unsaved changes',
        message: 'Switch to another template? Your unsaved changes to this template will be lost.',
      },
    })
      .afterClosed()
      .then((confirmed) => {
        if (confirmed === true) switchTo(index);
      });
  };

  const replace = (template: EmailTemplateRow) => {
    const list = templatesRef.current.map((t) => (t.key === template.key ? template : t));
    replaceTemplates(list);
    bind(list, selectedIndexRef.current);
  };

  const onSave = () => {
    const template = selected;
    if (!template) {
      return;
    }
    if (!body.trim()) {
      notifyError('Enter the email body.');
      return;
    }

    setIsSaving(true);
    firmSettingsService.saveEmailTemplate(template.key, subject.trim(), body).then(
      (saved) => {
        setIsSaving(false);
        if (!mounted.current) return;
        replace(saved);
        notifySuccess(`${saved.name} template saved`);
      },
      (error) => {
        setIsSaving(false);
        notifyError(error);
      },
    );
  };

  /** Puts the template back to the seeded default text. */
  const onReset = () => {
    const template = selected;
    if (!template) {
      return;
    }

    setIsSaving(true);
    firmSettingsService.resetEmailTemplate(template.key).then(
      (reset) => {
        setIsSaving(false);
        if (!mounted.current) return;
        replace(reset);
        notifySuccess(`${reset.name} template reset to the default`);
      },
      (error) => {
        if (template.key === DEFAULT_CIC_REPORT_TEMPLATE.key) {
          setIsSaving(false);
          if (!mounted.current) return;
          replace(DEFAULT_CIC_REPORT_TEMPLATE);
          notifySuccess(`${DEFAULT_CIC_REPORT_TEMPLATE.name} template reset to the default`);
          return;
        }
        setIsSaving(false);
        notifyError(error);
      },
    );
  };

  return (
    <div className="form wp-100">
      {/* Design review 2 Nov 2026: always editable; Save and Discard sit in the sticky page header (not a fixed footer) once something changed. */}
      <PageHeader title="Email Templates" dirty={!!selected && dirty} busy={isSaving} onSave={onSave} onDiscard={discard}>
        {selected && <InfoHint text="Change the subject or body, then click Save. Reset to Default puts the Capium text back." />}
        {selected && (
          <Button className="action-button secondary-button border-radius-6" disabled={isSaving} onClick={onReset}>
            <span>Reset to Default</span>
          </Button>
        )}
      </PageHeader>
      <LeaveGuard when={!!selected && dirty} />
      <div className="sidenav-details emoji-sec pt-5">
        <div className="highlight-details">
          <Tooltip title={TOOLTIP}>
            <Icon name="emoji_objects" className="text-yellow-200 highlight-icon" />
          </Tooltip>
        </div>
        <div className="sidenav-body">
          <TabGroup
            alignTabs="start"
            selectedIndex={selectedIndex}
            onSelectedIndexChange={onTabChange}
            tabs={templates.map((template) => ({ label: template.name }))}
          />

          {selected && (
            <div className="pt-1">
              <div className="form-field d-block mb-1">
                <p className="mb-1">Subject</p>
                {/* BUG-017: as in 3.0 the subject holds 80 characters and a blank one is saved as the default subject. */}
                <InputField
                  fieldClassName="wp-100"
                  autoComplete="off"
                  maxLength={EMAIL_SUBJECT_MAX_LENGTH}
                  placeholder="Email subject"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                />
              </div>

              <div className="d-flex gap-15">
                <div className="form-field textarea d-block">
                  <p className="mb-1">
                    Body<span className="text-red">*</span>
                  </p>
                  <RichTextEditor
                    id="emailTemplateEditor"
                    value={body}
                    config={editorConfig}
                    onChange={setBody}
                  />
                </div>
                {/* ap5-a5: the merge tags the accounts substitute; clicking one appends it to the body. 2 Nov review: the
                    same Tags tab as the note editors (closed until clicked) instead of the fixed side panel. */}
                <NoteTagsTab
                  groups={EMAIL_TEMPLATE_TAG_GROUPS}
                  onInsert={(tag) => setValues((current) => ({ ...current, body: appendCapiumTag(current.body, tag) }))}
                />
              </div>

              {selected.isCustomised && (
                <div className="info-message primary-message mt-1">
                  This template uses your firm's own text. Reset puts it back to the Capium default.
                </div>
              )}

            </div>
          )}

          {templates.length === 0 && <div className="info-message mt-1">No email templates are available.</div>}
        </div>
      </div>
    </div>
  );
}
